'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useChat } from '@/lib/hooks/useChat';
import { useSpeechToText } from '@/lib/hooks/useSpeechToText';
import { useTextToSpeech } from '@/lib/hooks/useTextToSpeech';
import { toast } from 'sonner';
import {
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  MicrophoneIcon,
  StopIcon,
  SpeakerWaveIcon,
  PhoneXMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesSolid } from '@heroicons/react/24/solid';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Message {
  role: 'user' | 'assistant';
  content: string;
  corrections?: GrammarCorrection[];
  timestamp: Date;
}

interface GrammarCorrection {
  original: string;
  corrected: string;
  explanation: string;
}

// ─── Available voices ─────────────────────────────────────────────────────────

const VOICES = [
  { id: 'nova', label: 'Nova', description: 'Energetic & bright' },
  { id: 'alloy', label: 'Alloy', description: 'Neutral & balanced' },
  { id: 'ash', label: 'Ash', description: 'Warm & conversational' },
  { id: 'coral', label: 'Coral', description: 'Clear & friendly' },
  { id: 'echo', label: 'Echo', description: 'Smooth & calm' },
  { id: 'fable', label: 'Fable', description: 'Expressive & storytelling' },
  { id: 'onyx', label: 'Onyx', description: 'Deep & authoritative' },
  { id: 'sage', label: 'Sage', description: 'Steady & neutral' },
  { id: 'shimmer', label: 'Shimmer', description: 'Light & gentle' },
] as const;

type VoiceId = (typeof VOICES)[number]['id'];

// ─── System prompt ────────────────────────────────────────────────────────────

const SYSTEM_PROMPT = `You are Marc's immersive English conversation partner for French speakers. Your role is to have natural, flowing English conversations while providing instant, encouraging grammar corrections.

CONVERSATION STYLE:
- Speak naturally in English at all times — never switch to French
- Be warm, encouraging, and conversational like a native English-speaking friend
- Keep responses concise (2-4 sentences) to maintain a natural conversation flow
- Ask follow-up questions to keep the conversation going
- Topics: daily life, travel, work, culture, Canadian life, current events

GRAMMAR CORRECTION FORMAT:
After your natural response, if the student made grammar errors, add a correction block in this EXACT format:

---CORRECTIONS---
ORIGINAL: [exact student phrase with error]
CORRECTED: [correct version]
EXPLANATION: [brief, encouraging explanation in English]
---END---

Rules for corrections:
- Only correct significant errors (not minor stylistic choices)
- Maximum 2 corrections per message to avoid overwhelming
- Be encouraging: "Great try!" or "Almost perfect!" before corrections
- If no errors, do NOT include the corrections block at all
- Focus on errors that French speakers commonly make (false friends, tense confusion, articles)`;

// ─── Parse corrections from AI response ──────────────────────────────────────

function parseResponse(raw: string): { text: string; corrections: GrammarCorrection[] } {
  const correctionMatch = raw.match(/---CORRECTIONS---([\s\S]*?)---END---/);
  const text = raw.replace(/---CORRECTIONS---[\s\S]*?---END---/, '').trim();
  const corrections: GrammarCorrection[] = [];

  if (correctionMatch) {
    const block = correctionMatch[1];
    const entries = block.split(/(?=ORIGINAL:)/g).filter(Boolean);
    for (const entry of entries) {
      const origMatch = entry.match(/ORIGINAL:\s*(.+)/);
      const corrMatch = entry.match(/CORRECTED:\s*(.+)/);
      const explMatch = entry.match(/EXPLANATION:\s*(.+)/);
      if (origMatch && corrMatch && explMatch) {
        corrections.push({
          original: origMatch[1].trim(),
          corrected: corrMatch[1].trim(),
          explanation: explMatch[1].trim(),
        });
      }
    }
  }

  return { text, corrections };
}

// ─── Strip correction block from live streaming text ─────────────────────────
function stripCorrectionBlock(raw: string): string {
  return raw.replace(/---CORRECTIONS---[\s\S]*?---END---/, '').replace(/---CORRECTIONS---[\s\S]*$/, '').trim();
}

// ─── Correction bubble ────────────────────────────────────────────────────────

function CorrectionBubble({ corrections }: { corrections: GrammarCorrection[] }) {
  if (!corrections.length) return null;
  return (
    <div className="mt-3 space-y-2">
      {corrections.map((c, i) => (
        <div key={i} className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <LightBulbIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-xs mb-1">
                <span className="line-through text-red-500 font-medium">{c.original}</span>
                <span className="text-muted-foreground">→</span>
                <span className="text-emerald-700 font-bold flex items-center gap-1">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  {c.corrected}
                </span>
              </div>
              <p className="text-xs text-amber-900 leading-snug">{c.explanation}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Typing indicator ─────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-end gap-3 justify-start">
      <div className="w-8 h-8 rounded-full navy-gradient flex items-center justify-center flex-shrink-0">
        <SparklesSolid className="w-4 h-4 text-amber-400" />
      </div>
      <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-4 py-3 shadow-card">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

// ─── Streaming bubble ─────────────────────────────────────────────────────────

function StreamingBubble({ text }: { text: string }) {
  return (
    <div className="flex items-end gap-3 justify-start">
      <div className="w-8 h-8 rounded-full navy-gradient flex items-center justify-center flex-shrink-0">
        <SparklesSolid className="w-4 h-4 text-amber-400" />
      </div>
      <div className="max-w-[80%] flex flex-col items-start">
        <div className="bg-[#F5F7FA] border border-border rounded-2xl rounded-bl-sm px-4 py-3 shadow-card">
          <p className="text-sm text-primary leading-relaxed whitespace-pre-wrap">
            {text}
            <span className="inline-block w-0.5 h-4 bg-primary/60 ml-0.5 animate-pulse align-middle" />
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Live conversation status badge ──────────────────────────────────────────

function LiveBadge({ phase }: { phase: 'listening' | 'thinking' | 'speaking' }) {
  const config = {
    listening: { color: 'bg-red-500', label: '🎙 Écoute…', pulse: true },
    thinking: { color: 'bg-amber-500', label: '🤔 Réflexion…', pulse: true },
    speaking: { color: 'bg-emerald-500', label: '🔊 Parle…', pulse: true },
  }[phase];

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white ${config.color} ${config.pulse ? 'animate-pulse' : ''}`}>
      {config.label}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AIConversationPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [apiHistory, setApiHistory] = useState<{ role: string; content: string }[]>([]);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [correctionCount, setCorrectionCount] = useState(0);
  const [exchangeCount, setExchangeCount] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);

  // ── Voice selection ──
  const [selectedVoice, setSelectedVoice] = useState<VoiceId>('nova');
  const [showVoicePicker, setShowVoicePicker] = useState(false);
  const selectedVoiceRef = useRef<VoiceId>('nova');
  useEffect(() => { selectedVoiceRef.current = selectedVoice; }, [selectedVoice]);

  // ── Manual microphone state ──
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // ── Live conversation loop state ──
  const [liveMode, setLiveMode] = useState(false);
  const [livePhase, setLivePhase] = useState<'listening' | 'thinking' | 'speaking' | null>(null);
  const liveModeRef = useRef(false);
  const liveMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const liveAudioChunksRef = useRef<Blob[]>([]);
  const liveSilenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveStreamRef = useRef<MediaStream | null>(null);

  // ── TTS state (OpenAI) ──
  const [isSpeaking, setIsSpeaking] = useState(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const prevIsLoadingRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o-mini', true);
  const { text: transcribedText, isLoading: isTranscribing, error: sttError, transcribe } = useSpeechToText('OPEN_AI', 'gpt-4o-transcribe');
  const { speak, isLoading: isTTSLoading, error: ttsError } = useTextToSpeech('OPEN_AI', 'gpt-4o-mini-tts');

  // ── Keep liveModeRef in sync ──
  useEffect(() => {
    liveModeRef.current = liveMode;
  }, [liveMode]);

  // ── TTS error toast ──
  useEffect(() => {
    if (ttsError) {
      toast.error('Erreur de synthèse vocale. Veuillez réessayer.');
      if (liveModeRef.current) {
        setIsSpeaking(false);
        setLivePhase('listening');
        startLiveListening();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsError]);

  // ── Error toasts ──
  useEffect(() => {
    if (error) {
      const msg = error.message || '';
      if (msg.includes('429') || msg.toLowerCase().includes('quota') || msg.toLowerCase().includes('rate') || msg.toLowerCase().includes('dépassé')) {
        toast.error('Limite de requêtes OpenAI atteinte. Attendez quelques secondes et réessayez, ou vérifiez votre plan sur platform.openai.com.', { duration: 6000 });
      } else if (msg.includes('401') || msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('invalide')) {
        toast.error('Clé API OpenAI invalide. Veuillez vérifier votre clé API dans les paramètres.', { duration: 6000 });
      } else {
        toast.error(msg || 'Une erreur est survenue. Veuillez réessayer.');
      }
      setIsStreaming(false);
      if (liveModeRef.current) {
        setLivePhase('listening');
        startLiveListening();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (sttError) {
      toast.error('Erreur de transcription. Veuillez réessayer.');
      if (liveModeRef.current) {
        setLivePhase('listening');
        startLiveListening();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sttError]);

  // ── When manual transcription completes, put text in input ──
  useEffect(() => {
    if (transcribedText && !liveModeRef.current) {
      setInput(transcribedText);
      inputRef.current?.focus();
    }
  }, [transcribedText]);

  // ── Auto-scroll ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, response, isLoading]);

  // ── Detect streaming start ──
  useEffect(() => {
    if (isLoading && !prevIsLoadingRef.current) {
      setIsStreaming(true);
      if (liveModeRef.current) setLivePhase('thinking');
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading]);

  // ── Commit completed response ──
  useEffect(() => {
    if (!isLoading && isStreaming && response && sessionStarted) {
      const { text, corrections } = parseResponse(response);

      const assistantMsg: Message = {
        role: 'assistant',
        content: text,
        corrections,
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === text) return prev;
        return [...prev, assistantMsg];
      });

      setApiHistory((prev) => [...prev, { role: 'assistant', content: response }]);
      setCorrectionCount((c) => c + corrections.length);
      setIsStreaming(false);

      // In live mode: speak the AI response using OpenAI TTS, then restart listening
      if (liveModeRef.current) {
        speakTextAndThenListen(text);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // ── Stop any currently playing audio ──
  const stopCurrentAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  // ── Speak text via OpenAI TTS, then restart live listening ──
  const speakTextAndThenListen = useCallback(async (text: string) => {
    if (!liveModeRef.current) return;
    setLivePhase('speaking');
    setIsSpeaking(true);

    stopCurrentAudio();

    try {
      const blob = await speak(text, selectedVoiceRef.current, {
        speed: 1.0,
        instructions: 'Speak naturally and conversationally, like a friendly native English speaker.',
      });

      if (!blob || !liveModeRef.current) {
        setIsSpeaking(false);
        if (liveModeRef.current) {
          setLivePhase('listening');
          startLiveListening();
        }
        return;
      }

      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        setIsSpeaking(false);
        if (liveModeRef.current) {
          setLivePhase('listening');
          startLiveListening();
        }
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        setIsSpeaking(false);
        if (liveModeRef.current) {
          setLivePhase('listening');
          startLiveListening();
        }
      };

      await audio.play();
    } catch {
      setIsSpeaking(false);
      if (liveModeRef.current) {
        setLivePhase('listening');
        startLiveListening();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speak, stopCurrentAudio]);

  // ── Start live mic listening ──
  const startLiveListening = useCallback(async () => {
    if (!liveModeRef.current) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      liveStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      liveMediaRecorderRef.current = mediaRecorder;
      liveAudioChunksRef.current = [];

      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      let silenceStart: number | null = null;
      let hasSpeech = false;
      const SILENCE_THRESHOLD = 10;
      const SILENCE_DURATION = 1800;
      const MIN_SPEECH_DURATION = 400;

      let speechStartTime = Date.now();
      let animFrameId: number;

      const checkSilence = () => {
        if (!liveModeRef.current) {
          audioCtx.close();
          return;
        }
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length) * 100;

        if (rms > SILENCE_THRESHOLD) {
          hasSpeech = true;
          silenceStart = null;
        } else if (hasSpeech && Date.now() - speechStartTime > MIN_SPEECH_DURATION) {
          if (silenceStart === null) {
            silenceStart = Date.now();
          } else if (Date.now() - silenceStart > SILENCE_DURATION) {
            audioCtx.close();
            if (mediaRecorder.state === 'recording') {
              mediaRecorder.stop();
            }
            return;
          }
        }
        animFrameId = requestAnimationFrame(checkSilence);
      };

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) liveAudioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        cancelAnimationFrame(animFrameId);
        stream.getTracks().forEach((t) => t.stop());
        liveStreamRef.current = null;

        if (!liveModeRef.current) return;

        if (!hasSpeech || liveAudioChunksRef.current.length === 0) {
          setLivePhase('listening');
          startLiveListening();
          return;
        }

        setLivePhase('thinking');
        const audioBlob = new Blob(liveAudioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], 'live.webm', { type: 'audio/webm' });

        try {
          const result = await transcribe(audioFile, { language: 'en' });
          const spokenText = (result as any)?.text || '';

          if (!spokenText.trim() || !liveModeRef.current) {
            if (liveModeRef.current) {
              setLivePhase('listening');
              startLiveListening();
            }
            return;
          }

          const userMsg: Message = {
            role: 'user',
            content: spokenText.trim(),
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, userMsg]);

          setApiHistory((prev) => {
            const newHistory = [...prev, { role: 'user', content: spokenText.trim() }];
            sendMessage(
              [{ role: 'system', content: SYSTEM_PROMPT }, ...newHistory],
              { max_completion_tokens: 400 }
            );
            setExchangeCount((c) => c + 1);
            return newHistory;
          });
        } catch {
          if (liveModeRef.current) {
            setLivePhase('listening');
            startLiveListening();
          }
        }
      };

      speechStartTime = Date.now();
      mediaRecorder.start(100);
      animFrameId = requestAnimationFrame(checkSilence);
    } catch {
      toast.error('Microphone non disponible. Vérifiez les permissions.');
      stopLiveMode();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcribe, sendMessage]);

  // ── Stop live mode completely ──
  const stopLiveMode = useCallback(() => {
    liveModeRef.current = false;
    setLiveMode(false);
    setLivePhase(null);

    if (liveMediaRecorderRef.current && liveMediaRecorderRef.current.state === 'recording') {
      liveMediaRecorderRef.current.stop();
    }
    if (liveStreamRef.current) {
      liveStreamRef.current.getTracks().forEach((t) => t.stop());
      liveStreamRef.current = null;
    }

    stopCurrentAudio();

    if (liveSilenceTimerRef.current) {
      clearTimeout(liveSilenceTimerRef.current);
    }
  }, [stopCurrentAudio]);

  // ── Toggle live conversation mode ──
  const handleToggleLiveMode = useCallback(async () => {
    if (liveMode) {
      stopLiveMode();
      return;
    }
    liveModeRef.current = true;
    setLiveMode(true);
    setLivePhase('listening');
    await startLiveListening();
  }, [liveMode, stopLiveMode, startLiveListening]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      stopLiveMode();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Start session ──
  const startSession = () => {
    setSessionStarted(true);
    const greetingMessages = [
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'user', content: "Hello! I\'m ready to practice my English. Can we start a conversation?" },
    ];
    setApiHistory([{ role: 'user', content: "Hello! I'm ready to practice my English. Can we start a conversation?" }]);
    sendMessage(greetingMessages, { max_completion_tokens: 300 });
    setExchangeCount(1);
  };

  // ── Send user message (manual text) ──
  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg: Message = {
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    const newHistory = [...apiHistory, { role: 'user', content: trimmed }];
    setApiHistory(newHistory);

    sendMessage(
      [{ role: 'system', content: SYSTEM_PROMPT }, ...newHistory],
      { max_completion_tokens: 400 }
    );

    setExchangeCount((c) => c + 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Manual microphone: start/stop recording ──
  const handleMicToggle = useCallback(async () => {
    if (liveMode) return;

    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
        await transcribe(audioFile, { language: 'en' });
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      toast.error('Microphone non disponible. Vérifiez les permissions.');
    }
  }, [isRecording, transcribe, liveMode]);

  // ── Manual TTS: read last AI message aloud via OpenAI ──
  const handleManualSpeak = useCallback(async () => {
    if (liveMode) return;

    if (isSpeaking) {
      stopCurrentAudio();
      return;
    }

    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant) {
      toast.error('Aucun message IA à lire.');
      return;
    }

    setIsSpeaking(true);
    try {
      const blob = await speak(lastAssistant.content, selectedVoice, {
        speed: 1.0,
        instructions: 'Speak naturally and conversationally, like a friendly native English speaker.',
      });

      if (!blob) {
        setIsSpeaking(false);
        return;
      }

      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        setIsSpeaking(false);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        setIsSpeaking(false);
      };

      await audio.play();
    } catch {
      setIsSpeaking(false);
    }
  }, [isSpeaking, messages, liveMode, speak, selectedVoice, stopCurrentAudio]);

  // ── Suggested starters ──
  const STARTERS = [
    "Tell me about your day",
    "What do you do for work?",
    "I want to practice job interviews",
    "Let\'s talk about travel",
    "Can you help me with small talk?",
    "I\'m at a restaurant and want to order",
    "Help me check in at a hotel",
    "I need to call a doctor's office",
    "Let\'s practice a business meeting",
    "I want to talk about my hobbies",
    "Help me ask for directions",
    "I\'m shopping and need help",
    "Let\'s talk about the weather",
    "I want to introduce myself professionally",
    "Help me write a professional email",
    "Let\'s discuss a film or TV show",
    "I want to practice phone conversations",
    "Help me negotiate a price",
    "Let's talk about family life",
    "I need to complain politely",
    "Help me at the airport",
    "Let\'s discuss Canadian culture",
    "I want to practice apologising",
    "Help me make plans with friends",
    "Let\'s talk about food and cooking",
    "I want to discuss current events",
    "Help me at a job interview",
    "Let\'s practice giving opinions",
    "I want to talk about sports",
    "Help me in a medical emergency",
  ];

  // ─── Pre-session screen ───────────────────────────────────────────────────

  if (!sessionStarted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-lg w-full">
            <Link
              href="/dashboard"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Retour au tableau de bord
            </Link>

            <div className="bg-[#1B2A4A] rounded-3xl p-8 text-center shadow-2xl">
              <div className="w-16 h-16 rounded-2xl bg-amber-400/20 flex items-center justify-center mx-auto mb-5">
                <SparklesSolid className="w-8 h-8 text-amber-400" />
              </div>
              <h1 className="text-2xl font-extrabold text-white mb-2">
                Conversation Partner IA
              </h1>
              <p className="text-sm text-white/60 leading-relaxed mb-6">
                Pratiquez l'anglais naturellement avec votre partenaire IA. Chaque message est analysé en temps réel — corrections grammaticales instantanées, feedback immersif, progression visible.
              </p>

              {/* Voice selector on pre-session screen */}
              <div className="mb-6">
                <p className="text-xs text-white/50 mb-2 font-medium">Choisissez la voix de l'IA</p>
                <div className="grid grid-cols-3 gap-2">
                  {VOICES.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setSelectedVoice(v.id)}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${
                        selectedVoice === v.id
                          ? 'bg-amber-400 text-[#1B2A4A] border-amber-400'
                          : 'bg-white/10 text-white/70 border-white/20 hover:bg-white/20'
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-white/30 mt-2">
                  {VOICES.find((v) => v.id === selectedVoice)?.description}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-8">
                {[
                  { icon: <MicrophoneIcon className="w-5 h-5" />, label: 'Conversation naturelle' },
                  { icon: <CheckCircleIcon className="w-5 h-5" />, label: 'Corrections instantanées' },
                  { icon: <LightBulbIcon className="w-5 h-5" />, label: 'Feedback immersif' },
                ].map((f) => (
                  <div key={f.label} className="bg-white/10 rounded-xl p-3 flex flex-col items-center gap-2">
                    <span className="text-amber-400">{f.icon}</span>
                    <span className="text-xs text-white/70 font-medium text-center leading-tight">{f.label}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={startSession}
                className="w-full py-3.5 bg-amber-400 hover:bg-amber-300 text-[#1B2A4A] font-extrabold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                <SparklesIcon className="w-5 h-5" />
                Démarrer la conversation
              </button>

              <p className="text-xs text-white/30 mt-4">
                Méthode immersion · Marc Herbaut · Anglaispourvous
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ─── Derive live streaming text ───────────────────────────────────────────
  const liveText = isStreaming && response ? stripCorrectionBlock(response) : '';

  // ─── Chat screen ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 flex flex-col max-w-3xl mx-auto w-full px-4 py-6 gap-4">

        {/* ── Top bar ── */}
        <div className="flex items-center justify-between">
          <Link
            href="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Dashboard
          </Link>
          <div className="flex items-center gap-3">
            {liveMode && livePhase && <LiveBadge phase={livePhase} />}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                En ligne
              </span>
              <span className="text-border">·</span>
              <span>{exchangeCount} échanges</span>
              {correctionCount > 0 && (
                <>
                  <span className="text-border">·</span>
                  <span className="text-amber-600 font-semibold">{correctionCount} corrections</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Messages area ── */}
        <div className="flex-1 bg-white rounded-2xl border border-border shadow-card overflow-y-auto p-5 space-y-5 min-h-[400px] max-h-[calc(100vh-320px)]">

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex items-end gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full navy-gradient flex items-center justify-center flex-shrink-0">
                  <SparklesSolid className="w-4 h-4 text-amber-400" />
                </div>
              )}

              <div className={`max-w-[80%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col`}>
                <div
                  className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user' ?'bg-[#1B2A4A] text-white rounded-br-sm' :'bg-[#F5F7FA] border border-border text-primary rounded-bl-sm shadow-card'
                  }`}
                >
                  {msg.content}
                </div>

                {msg.role === 'assistant' && msg.corrections && msg.corrections.length > 0 && (
                  <CorrectionBubble corrections={msg.corrections} />
                )}

                <span className="text-[10px] text-muted-foreground mt-1 px-1">
                  {typeof window !== 'undefined' ? msg.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>

              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-amber-700">
                  M
                </div>
              )}
            </div>
          ))}

          {isStreaming && liveText && <StreamingBubble text={liveText} />}
          {isStreaming && !liveText && <TypingIndicator />}

          {error && (
            <div className="flex items-start gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
              </div>
              <div className="max-w-[80%] bg-red-50 border border-red-200 rounded-2xl rounded-bl-sm px-4 py-3">
                <p className="text-sm font-semibold text-red-700 mb-1">Erreur de connexion IA</p>
                {(error.message.includes('429') || error.message.toLowerCase().includes('quota') || error.message.toLowerCase().includes('rate')) ? (
                  <p className="text-xs text-red-600 leading-relaxed">
                    Le quota OpenAI est dépassé. Veuillez vérifier votre clé API et votre plan de facturation sur{' '}
                    <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                      platform.openai.com
                    </a>.
                  </p>
                ) : (
                  <p className="text-xs text-red-600 leading-relaxed">{error.message}</p>
                )}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Suggested starters ── */}
        {messages.length === 1 && messages[0].role === 'assistant' && !isStreaming && (
          <div className="flex flex-wrap gap-2">
            {STARTERS.map((s) => (
              <button
                key={s}
                onClick={() => {
                  setInput(s);
                  inputRef.current?.focus();
                }}
                className="text-xs px-3 py-1.5 rounded-full border border-border bg-white text-secondary hover:border-accent hover:text-accent hover:bg-amber-50/40 transition-all font-medium"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* ── Live conversation banner ── */}
        {liveMode && (
          <div className="bg-[#1B2A4A] rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                livePhase === 'listening' ? 'bg-red-400 animate-pulse' :
                livePhase === 'thinking' ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400 animate-pulse'
              }`} />
              <div>
                <p className="text-xs font-bold text-white">
                  {livePhase === 'listening' && '🎙 Parlez maintenant…'}
                  {livePhase === 'thinking' && '🤔 L\'IA réfléchit…'}
                  {livePhase === 'speaking' && `🔊 L'IA parle… (${VOICES.find((v) => v.id === selectedVoice)?.label})`}
                </p>
                <p className="text-[10px] text-white/50 mt-0.5">
                  Conversation continue — parlez, l'IA répond, puis vous réécoutez automatiquement
                </p>
              </div>
            </div>
            <button
              onClick={stopLiveMode}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-500 hover:bg-red-400 text-white rounded-xl text-xs font-bold transition-colors flex-shrink-0"
            >
              <PhoneXMarkIcon className="w-4 h-4" />
              Terminer
            </button>
          </div>
        )}

        {/* ── Input area ── */}
        <div className={`bg-white rounded-2xl border shadow-card p-3 flex flex-col gap-2 transition-all ${liveMode ? 'border-amber-300 opacity-60 pointer-events-none' : 'border-border'}`}>
          {/* Text input row */}
          <div className="flex items-end gap-3">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                liveMode ? '🎙 Mode conversation live actif…' :
                isRecording ? '🎙 Enregistrement en cours… cliquez Stop pour terminer': isTranscribing ?'⏳ Transcription en cours…': 'Type in English… (Enter to send, Shift+Enter for new line)'
              }
              disabled={isLoading || isRecording || isTranscribing || liveMode}
              rows={1}
              className="flex-1 resize-none text-sm text-primary placeholder:text-muted-foreground bg-transparent outline-none leading-relaxed py-1.5 max-h-32 overflow-y-auto disabled:opacity-50"
              style={{ minHeight: '40px' }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading || liveMode}
              className="w-10 h-10 rounded-xl navy-gradient flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              title="Envoyer"
            >
              {isLoading ? (
                <StopIcon className="w-4 h-4 text-white" />
              ) : (
                <PaperAirplaneIcon className="w-4 h-4 text-white" />
              )}
            </button>
          </div>

          {/* Bottom action buttons */}
          <div className="flex items-center gap-2 pt-1 border-t border-border/50">
            {/* Manual microphone button */}
            <button
              onClick={handleMicToggle}
              disabled={isLoading || isTranscribing || liveMode}
              title={isRecording ? 'Arrêter l\'enregistrement' : 'Parler en anglais'}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                isRecording
                  ? 'bg-red-500 text-white animate-pulse' :'bg-[#1B2A4A]/8 text-[#1B2A4A] hover:bg-[#1B2A4A]/15 border border-[#1B2A4A]/20'
              }`}
            >
              {isRecording ? (
                <>
                  <StopIcon className="w-4 h-4" />
                  Stop
                </>
              ) : isTranscribing ? (
                <>
                  <span className="w-4 h-4 border-2 border-[#1B2A4A]/40 border-t-[#1B2A4A] rounded-full animate-spin" />
                  Transcription…
                </>
              ) : (
                <>
                  <MicrophoneIcon className="w-4 h-4" />
                  Microphone
                </>
              )}
            </button>

            {/* Manual TTS button */}
            <button
              onClick={handleManualSpeak}
              disabled={isLoading || isTTSLoading || messages.filter((m) => m.role === 'assistant').length === 0 || liveMode}
              title={isSpeaking ? 'Arrêter la lecture' : 'Écouter la dernière réponse IA'}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                isSpeaking || isTTSLoading
                  ? 'bg-emerald-500 text-white animate-pulse' :'bg-[#1B2A4A]/8 text-[#1B2A4A] hover:bg-[#1B2A4A]/15 border border-[#1B2A4A]/20'
              }`}
            >
              <SpeakerWaveIcon className="w-4 h-4" />
              {isTTSLoading ? 'Génération…' : isSpeaking ? 'Stop lecture' : 'Écouter'}
            </button>

            {/* Voice picker */}
            <div className="relative ml-auto">
              <button
                onClick={() => setShowVoicePicker((v) => !v)}
                disabled={liveMode}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-[#1B2A4A]/8 text-[#1B2A4A] hover:bg-[#1B2A4A]/15 border border-[#1B2A4A]/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                title="Changer la voix"
              >
                <SpeakerWaveIcon className="w-3.5 h-3.5" />
                {VOICES.find((v) => v.id === selectedVoice)?.label}
                <ChevronDownIcon className="w-3 h-3" />
              </button>
              {showVoicePicker && (
                <div className="absolute bottom-full right-0 mb-2 bg-white border border-border rounded-xl shadow-lg p-2 z-10 min-w-[160px]">
                  {VOICES.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => { setSelectedVoice(v.id); setShowVoicePicker(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                        selectedVoice === v.id
                          ? 'bg-[#1B2A4A] text-white font-bold'
                          : 'text-primary hover:bg-[#1B2A4A]/8'
                      }`}
                    >
                      <span className="font-semibold">{v.label}</span>
                      <span className={`ml-1.5 ${selectedVoice === v.id ? 'text-white/60' : 'text-muted-foreground'}`}>
                        — {v.description}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Live conversation toggle button ── */}
        <button
          onClick={handleToggleLiveMode}
          disabled={isLoading && !liveMode}
          className={`w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2.5 transition-all shadow-card ${
            liveMode
              ? 'bg-red-500 hover:bg-red-400 text-white' :'bg-[#1B2A4A] hover:bg-[#243660] text-white'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {liveMode ? (
            <>
              <PhoneXMarkIcon className="w-5 h-5" />
              Terminer la conversation live
            </>
          ) : (
            <>
              <MicrophoneIcon className="w-5 h-5" />
              🎙 Démarrer la conversation live (2 voies)
            </>
          )}
        </button>

        {/* ── Footer hint ── */}
        <p className="text-center text-xs text-muted-foreground">
          <ExclamationTriangleIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-amber-500" />
          Méthode immersion Marc Herbaut — parlez librement, les corrections arrivent naturellement.
        </p>
      </main>

      <Footer />
    </div>
  );
}
